import {
  CommentInputValue,
  CommentModel,
  CommentService,
  ThreadModel,
  ThreadService,
  ThreadStateEnum,
} from '@anecdotes/commenting';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TenantSubDomainExtractorService, UserClaims } from '@anecdotes/auth-core';
import { firstValueFrom, Observable } from 'rxjs';
import { AuditorsFilter, CommentFilterOption, MentionedThreadsFilter, ThreadViewModel } from '../../models';
import { CommentPanelManagerService } from '../comment-panel-manager/comment-panel-manager.service';
import { CommentingUserEventsService } from '../commenting-user-events/commenting-user-events.service';
import { FeatureFlagsService } from '@anecdotes/feature-flags';

@Injectable()
export class CommentingFacadeService {
  constructor(
    private threadService: ThreadService,
    private commentService: CommentService,
    @Inject(DOCUMENT) private document: Document,
    private tenantSubDomainExtractorService: TenantSubDomainExtractorService,
    private commentPanelManagerService: CommentPanelManagerService,
    private commentingUserEventsService: CommentingUserEventsService,
    private featureFlagsService: FeatureFlagsService
  ) {}

  getThreadById(threadId: string): Observable<ThreadModel> {
    return this.threadService.getById(threadId);
  }

  getCommentsByThreadId(threadId: string): Observable<CommentModel[]> {
    return this.commentService.getByThreadId(threadId);
  }

  getAllComments(): Observable<CommentModel[]> {
    return this.commentService.getAll();
  }

  async createThreadAsync(
    resourceType: string,
    resourceId: string,
    commentInputValue: CommentInputValue
  ): Promise<ThreadModel> {
    const relevantResource = await firstValueFrom(this.commentPanelManagerService
      .getResourceToCreateThreadFor());
    const extraParamsFromResource = relevantResource.extraParams ? relevantResource.extraParams : {};
    await this.commentingUserEventsService.trackThreadCreationEventAsync(resourceType, resourceId, !!commentInputValue.mentionedUsers.length);

    return this.threadService.createAsync({
      resourceId: resourceId,
      resourceType: resourceType,
      body: commentInputValue.body,
      extraParams: {
        ...extraParamsFromResource,
        url: this.getUrl(),
        resourceName: relevantResource.resourceDisplayName,
        resourceType: relevantResource.resourceType,
        messageBody: commentInputValue.body
      },
      mentionedEmails: commentInputValue.mentionedUsers?.map((user) => user.email),
    });
  }

  async createCommentAsync(threadId: string, commentInputValue: CommentInputValue, user: UserClaims): Promise<CommentModel> {
    const resources = await this.getThreadViewModelsAsync();
    const relevantResource = resources.find((thread) => thread.threadId === threadId);
    const extraParamsFromResource = relevantResource.extraParams ? relevantResource.extraParams : {};
    await this.commentingUserEventsService.trackReplyCreationEventAsync(
      relevantResource.resourceType,
      relevantResource.resourceId,
      !!commentInputValue.mentionedUsers.length,
      relevantResource.comments.length,
    );

    return this.commentService.createAsync({
      threadId,
      body: commentInputValue.body,
      extraParams: {
        ...extraParamsFromResource,
        url: this.getUrl(),
        resourceName: relevantResource.resourceDisplayName,
        resourceType: relevantResource.resourceType,
        messageBody: commentInputValue.body
      },
      mentionedEmails: commentInputValue.mentionedUsers?.map((user) => user.email),
      commenterId: user.user_id,
      commenterName: user.name,
    });
  }

  async resolveThreadAsync(threadId: string): Promise<void> {
    await this.commentingUserEventsService.trackResolveThreadEventAsync(threadId);

    if (await this.isAdmin()) {
      return this.threadService.batchMarkResolvedAsync([threadId]);
    }

    return this.threadService.markResolvedAsync(threadId);
  }

  async activateThreadAsync(threadId: string): Promise<void> {
    await this.commentingUserEventsService.trackActivateThreadEventAsync(threadId);

    if (await this.isAdmin()) {
      return this.threadService.batchMarkActiveAsync([threadId]);
    }

    return this.threadService.markActiveAsync(threadId);
  }

  async editThreadAsync(threadId: string, commentInputValue: CommentInputValue): Promise<void> {
    const resources = await this.getThreadViewModelsAsync();
    const relevantResource = resources.find((thread) => thread.threadId === threadId);
    const extraParamsFromResource = relevantResource.extraParams ? relevantResource.extraParams : {};
    this.commentingUserEventsService.trackEditThreadEventAsync(threadId, !!commentInputValue.mentionedUsers.length);
    return await this.threadService.updateBodyWithExtraParams(
      threadId,
      commentInputValue.body,
      commentInputValue.mentionedUsers?.map((user) => user.email),
      {
        ...extraParamsFromResource,
        url: this.getUrl(),
        resourceName: relevantResource.resourceDisplayName,
        resourceType: relevantResource.resourceType,
        messageBody: commentInputValue.body
      }
    );
  }

  async editCommentAsync(commentId: string, commentInputValue: CommentInputValue): Promise<void> {
    const comment = await firstValueFrom(this.commentService.getById(commentId));
    const resources = await this.getThreadViewModelsAsync();
    const relevantResource = resources.find((thread) => thread.threadId === comment.threadId);
    const extraParamsFromResource = relevantResource.extraParams ? relevantResource.extraParams : {};

    return this.commentService.updateBodyWithExtraParams(
      commentId,
      commentInputValue.body,
      commentInputValue.mentionedUsers?.map((user) => user.email),
      {
        ...extraParamsFromResource,
        url: this.getUrl(),
        resourceName: relevantResource.resourceDisplayName,
        resourceType: relevantResource.resourceType,
        messageBody: commentInputValue.body
      }
    );
  }

  async deleteComment(commentId: string): Promise<void> {
    return this.commentService.deleteAsync(commentId);
  }

  async deleteThread(threadId: string): Promise<void> {
    await this.commentingUserEventsService.trackDeleteThreadEventAsync(threadId);

    if (await this.isAdmin()) {
      return this.threadService.batchDeleteAsync([threadId]);
    }

    return this.threadService.deleteAsync(threadId);
  }

  async resolveAllActiveThreads(): Promise<void> {
    const threadViewModels = await this.getThreadViewModelsAsync();
    const activeThreadIds = threadViewModels
      .filter((thread) => thread.threadState === ThreadStateEnum.Active)
      .map((thread) => thread.threadId);

    await this.commentingUserEventsService.trackResolveAllEventAsync(activeThreadIds.length);
    await this.threadService.batchMarkResolvedAsync(activeThreadIds);
  }

  async resolveAllActiveThreadsOfResource(resourceId: string, resourceType: string): Promise<void> {
    const threadViewModels = await this.getThreadViewModelsAsync();
    const activeThreadIds = threadViewModels
      .filter((viewModel: ThreadViewModel) => {
        const isActiveThread = viewModel.threadState === ThreadStateEnum.Active;
        const isThreadOfResource = viewModel.resourceId === resourceId && viewModel.resourceType === resourceType;

        return isActiveThread && isThreadOfResource;
      })
      .map((viewModel: ThreadViewModel) => viewModel.threadId);

    await this.commentingUserEventsService.trackResolveAllEventAsync(activeThreadIds.length);
    await this.threadService.batchMarkResolvedAsync(activeThreadIds);
  }

  async deleteAllResolvedThreads(): Promise<void> {
    const threadViewModels = await this.getThreadViewModelsAsync();
    const activeThreadIds = threadViewModels
      .filter((thread) => thread.threadState === ThreadStateEnum.Resolved)
      .map((thread) => thread.threadId);
    await this.commentingUserEventsService.trackDeleteAllResolvedEvent(activeThreadIds.length);
    await this.threadService.batchDeleteAsync(activeThreadIds);
  }

  private getUrl(): string {
    return this.tenantSubDomainExtractorService.getCurrentTenantUrl() + this.document.location.pathname;
  }

  private isAdmin(): Promise<boolean> {
    return this.featureFlagsService.IsEnabledAsync('@commenting-full-access');
  }

  private getThreadViewModelsAsync(): Promise<ThreadViewModel[]> {
    return firstValueFrom(this.commentPanelManagerService.getCommentingResources());
  }

  async trackPanelFiltering(threadState: CommentFilterOption): Promise<void> {
    switch (threadState) {
      case ThreadStateEnum.Active:
        return await this.commentingUserEventsService.trackFilterToActiveThreadEventAsync();
      case ThreadStateEnum.Resolved:
        return await this.commentingUserEventsService.trackFilterToResolvedThreadEventAsync();
      case MentionedThreadsFilter:
        return await this.commentingUserEventsService.trackFilterToMentionedThreadEventAsync();
      case AuditorsFilter:
        return await this.commentingUserEventsService.trackFilterToVisibleForAuditorThreadEventAsync();
    }
  }
}
